/**
 * Кто говорит реплику перевода — лектор или зал, и каким голосом (проба P50m10d, правка E6).
 *
 * У строк редактора голос не был подписан: переводчик хотел переозвучить ♪ реплику лектора, а попал в реплику
 * зала (20261103210219893401, male1 — голос Michael), потому что строки выглядели одинаково. Теперь строка перевода
 * говорит это сама: «лектор», «зал · Michael», «зал · Matilda» (имя голоса — из пометки нового голоса эфира
 * `_v4_voice` в json реплики), у голоса зала без имени — «зал · мужчина» / «зал · женщина».
 *
 * Роли — те же, что у сервера озвучки (translate-python `stream_voice_forward.hall_role`, TAG `stream_voice`):
 * `male` — лектор, `female`/`female1` — женщина из зала, любая другая метка — мужчина из зала.
 */

export interface CueSpeaker {
    who: "lecturer" | "hall";
    /** Метка голоса реплики (`voice` её json): ею ♪ и озвучит. */
    voice: string;
    /** Имя голоса (Michael, Matilda) — только если пометка дубля о той же роли. */
    name: string | null;
    gender: "m" | "f";
}

const LECTURER = "male";
const HALL_WOMAN = ["female", "female1"];
const NOT_A_VOICE = ["", "none", "auto", "unknown"];

const roleOf = (voice: string): "lecturer" | "male1" | "female" =>
    voice === LECTURER ? "lecturer" : HALL_WOMAN.includes(voice) ? "female" : "male1";

/** Кто говорит реплику по её json перевода; голоса нет («none», пусто) — null, строка молчит. */
export const cueSpeakerOf = (target: { voice?: unknown; _v4_voice?: unknown } | null | undefined): CueSpeaker | null => {
    const voice = typeof target?.voice === "string" ? target.voice.trim() : "";
    if (NOT_A_VOICE.includes(voice.toLowerCase())) return null;
    const role = roleOf(voice);
    if (role === "lecturer") return { who: "lecturer", voice, name: null, gender: "m" };
    const stamp =
        target?._v4_voice && typeof target._v4_voice === "object" ? (target._v4_voice as Record<string, unknown>) : null;
    const stampRole = typeof stamp?.role === "string" && stamp.role.trim() ? roleOf(stamp.role.trim()) : null;
    // Пометка о другом голосе (дубль остался от прежнего голоса реплики) — имени не берём, называем роль.
    const name =
        stamp && stampRole === role && typeof stamp.name === "string" && stamp.name.trim() ? stamp.name.trim() : null;
    return { who: "hall", voice, name, gender: role === "female" ? "f" : "m" };
};

/** Подпись строки: «лектор», «зал · Michael», «зал · мужчина». */
export const cueSpeakerLabel = (speaker: CueSpeaker, en: boolean): string => {
    if (speaker.who === "lecturer") return en ? "lecturer" : "лектор";
    const name = speaker.name || (speaker.gender === "f" ? (en ? "woman" : "женщина") : en ? "man" : "мужчина");
    return `${en ? "hall" : "зал"} · ${name}`;
};

/** Подсказка подписи: кто говорит и каким голосом ♪ переозвучит. */
export const cueSpeakerTitle = (speaker: CueSpeaker, en: boolean): string => {
    if (speaker.who === "lecturer") {
        return en
            ? "The lecturer speaks this cue — ♪ voices it again with the lecturer's voice."
            : "Эту реплику говорит лектор — ♪ переозвучит её голосом лектора.";
    }
    const person = speaker.gender === "f" ? (en ? "a woman" : "женщина") : en ? "a man" : "мужчина";
    const voice = speaker.name ? (en ? ` (voice ${speaker.name})` : ` (голос ${speaker.name})`) : "";
    return en
        ? `Someone from the hall speaks this cue — ${person}${voice}, not the lecturer. ♪ voices it again with this same voice.`
        : `Эту реплику говорит зал — ${person}${voice}, не лектор. ♪ переозвучит её этим же голосом.`;
};

/** Та же ли подпись — строка оригинала стоит на месте, а голос перевода сменился (mergeTrackArrays). */
export const sameCueSpeaker = (a: CueSpeaker | null | undefined, b: CueSpeaker | null | undefined): boolean =>
    a === b || (!a && !b) || (!!a && !!b && a.who === b.who && a.voice === b.voice && a.name === b.name);
